import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_URL, PRODUCT_NAME } from "@/lib/brand";
import { userTools } from "@/lib/tools";
import ThemeSwitcher from "@/components/ThemeSwitcher";

const Nav = [
  { to: "/app",            label: "Painel",         icon: "fa-gauge-high",       end: true },
  { to: "/app/dashboard-avancado", label: "Dashboard Avançado", icon: "fa-chart-line" },
  { to: "/app/empresas",   label: "Empresas",       icon: "fa-building" },
  { to: "/app/avaliacoes", label: "Avaliações",     icon: "fa-clipboard-list" },
  { to: "/app/absenteismo", label: "Absenteísmo",   icon: "fa-user-doctor" },
  { to: "/app/equipe",     label: "Equipe",         icon: "fa-user-group" },
  { to: "/app/downloads",  label: "Downloads",      icon: "fa-cloud-arrow-down" },
  { to: "/app/planos",     label: "Planos",         icon: "fa-tags" },
];

const AdminNav = [
  { to: "/app/admin/usuarios",      label: "Usuários",      icon: "fa-users-gear" },
  { to: "/app/admin/leads",         label: "Leads",         icon: "fa-fire" },
  { to: "/app/admin/financeiro",    label: "Financeiro",    icon: "fa-chart-line" },
  { to: "/app/admin/downloads",     label: "Materiais",     icon: "fa-cloud-arrow-up" },
  { to: "/app/admin/auditoria",     label: "Auditoria",     icon: "fa-clipboard-check" },
  { to: "/app/admin/configuracoes", label: "Configurações", icon: "fa-sliders" },
];

const ROLE_LABELS = { admin: "Administrador", manager: "Gestor", client: "Cliente" };

function NavItem({ to, end, icon, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) => `so-nav-item ${isActive ? "active" : ""}`}
    >
      <i className={`fa-solid ${icon}`}></i>
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--so-bg)" }}>
      {/* === Sidebar (light, premium) === */}
      <aside
        className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col no-print"
        data-testid="app-sidebar"
      >
        <Link to="/app" className="px-6 pt-6 pb-5 block" data-testid="sidebar-brand">
          <div className="so-logo-halo">
            <img src={LOGO_URL} alt="Sollute Ocupacional" className="w-28 h-auto" data-testid="sidebar-logo" />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span
              className="text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-md"
              style={{
                background: "var(--so-primary-soft)",
                color: "var(--so-primary)",
              }}
            >
              {PRODUCT_NAME}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-2 leading-snug">
            Riscos Psicossociais
          </div>
        </Link>

        <div className="h-px bg-slate-100 mx-4" />

        <nav className="flex-1 py-3 overflow-y-auto">
          <div className="overline text-[9.5px] text-slate-400 px-6 pt-2 pb-1.5">Workspace</div>
          {Nav.map((n) => (
            <NavItem key={n.to} {...n} />
          ))}

          {user?.role === "admin" && (
            <>
              <div className="overline text-[9.5px] text-slate-400 px-6 pt-5 pb-1.5">Administração</div>
              {AdminNav.map((n) => (
                <NavItem key={n.to} {...n} />
              ))}
            </>
          )}

          {/* Optional tools the admin has enabled for this user */}
          {userTools(user).length > 0 && (
            <>
              <div className="overline text-[9.5px] text-slate-400 px-6 pt-5 pb-1.5">Ferramentas</div>
              {userTools(user).map((t) => (
                <NavItem
                  key={t.id}
                  to={`/app/ferramentas/${t.id}`}
                  icon={t.icon}
                  label={t.short}
                />
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-slate-100 p-4 space-y-3">
          {user?.role === "admin" && <ThemeSwitcher />}
          <div
            className="rounded-xl p-3"
            style={{ background: "var(--so-primary-soft)" }}
            data-testid="sidebar-user-card"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{ background: "var(--so-gradient)" }}
              >
                {(user?.name || "?").trim().slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
            {user?.role && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: "var(--so-primary)" }}>
                  {ROLE_LABELS[user.role] || user.role}
                </span>
                {user.is_trial && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                    data-testid="sidebar-trial-badge"
                  >
                    Demonstração
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="logout-button"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors"
          >
            <i className="fa-solid fa-right-from-bracket"></i> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <RenewalBanner user={user} />
        <Outlet />
      </main>
    </div>
  );
}

/* ----------------- Renewal monitoring banner -----------------
 * Shows above every authenticated page when:
 *   - User is paid (not trial) AND
 *   - paid_until is within the next 60 days OR already expired
 *
 * Three states with escalating colors:
 *   • 30-60 days left: subtle slate banner (informational)
 *   • 7-29 days left:  amber warning (action recommended)
 *   • <7 days or expired: red, dismissable=false (action urgent)
 */
function RenewalBanner({ user }) {
  if (!user || user.is_trial || !user.paid_until) return null;

  const paidUntil = new Date(user.paid_until);
  if (Number.isNaN(paidUntil.getTime())) return null;
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((paidUntil - now) / msPerDay);

  // Don't show banner if more than 60 days remain
  if (daysLeft > 60) return null;

  const expired = daysLeft < 0;
  const urgent = daysLeft >= 0 && daysLeft <= 7;
  const warn = daysLeft >= 8 && daysLeft <= 29;

  const tone = expired
    ? { bg: "bg-rose-900 text-white border-rose-700",
        title: "Seu plano expirou",
        msg: `O período de 12 meses contratados em ${new Date(user.paid_since || user.paid_until).toLocaleDateString("pt-BR")} venceu em ${paidUntil.toLocaleDateString("pt-BR")}. Renove para continuar usando a plataforma.`,
        cta: "Renovar agora",
        icon: "fa-circle-exclamation" }
    : urgent
    ? { bg: "bg-rose-50 text-rose-900 border-rose-300",
        title: `Renovação em ${daysLeft === 0 ? "hoje" : daysLeft === 1 ? "amanhã" : `${daysLeft} dias`}`,
        msg: `Seu plano vence em ${paidUntil.toLocaleDateString("pt-BR")}. Renove agora para não perder acesso aos seus relatórios AEP, absenteísmo e plano anual SST.`,
        cta: "Renovar plano",
        icon: "fa-triangle-exclamation" }
    : warn
    ? { bg: "bg-amber-50 text-amber-900 border-amber-300",
        title: `Plano vence em ${daysLeft} dias`,
        msg: `Renovação recomendada antes de ${paidUntil.toLocaleDateString("pt-BR")} para manter acesso ininterrupto.`,
        cta: "Renovar antecipado",
        icon: "fa-bell" }
    : { bg: "bg-slate-50 text-slate-700 border-slate-300",
        title: `Plano ativo · vence em ${daysLeft} dias`,
        msg: `${paidUntil.toLocaleDateString("pt-BR")} — você pode renovar com antecedência para acumular dias e manter desconto promocional.`,
        cta: "Ver opções de renovação",
        icon: "fa-info-circle" };

  return (
    <div className={`border-b ${tone.bg} no-print`} data-testid="renewal-banner"
         data-renewal-state={expired ? "expired" : urgent ? "urgent" : warn ? "warn" : "info"}>
      <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <i className={`fa-solid ${tone.icon} text-lg shrink-0`}></i>
          <div className="min-w-0">
            <div className="font-bold text-sm">{tone.title}</div>
            <div className="text-xs opacity-90">{tone.msg}</div>
          </div>
        </div>
        <Link to="/app/planos"
              data-testid="renewal-banner-cta"
              className={`btn text-xs font-bold ${expired || urgent ? "bg-white text-rose-900 hover:bg-rose-50" : "btn-primary"}`}>
          <i className="fa-solid fa-credit-card"></i> {tone.cta}
        </Link>
      </div>
    </div>
  );
}
